import type { Msg, Session } from '@/store/client';
import { MessageStatus, MsgPayloadType } from '@/store/client';
import {
  MsgEncryptFailedPreview,
  MsgStickerPreview,
  MsgTextPreview,
  MsgMediaCallPreview,
  MsgMediaHangUpPreview,
  MsgMediaCallTimeoutPreview,
  MsgMediaCallCancelledPreview,
  MsgMediaCallRejectedPreview,
  MsgMediaCallNetworkErrorPreview,
  MsgGroupCreatePreview,
  MsgGroupUpdatePreview,
  MsgGroupDisbandPreview,
  MsgGroupInvitePreview,
  MsgGroupRemoveMemberPreview,
  MsgGroupMemberQuitPreview,
  MsgGroupMediaClosePreview,
  MsgGroupUpdateJoinPreview,
  MsgGroupUpdateInfoPreview,
  MsgExpireDurationPreview,
  MsgLuckyBoxClaimedPreview,
  MsgImagePreview,
  MsgFilePreview,
  MsgLuckyBoxPreview,
  MsgTransferPreview,
  MsgUnknownPreview,
} from '../messages';
import { MsgGroupUpdateAdminsPreview } from '../messages/msg-group-update-admins';
import { MsgGroupUpdateDescriptionPreview } from '../messages/msg-group-update-description';
import { MsgGroupRestrictedPreview } from '../messages/msg-group-restricted';
import { MsgGroupUnrestrictedPreview } from '../messages/msg-group-unrestricted';
import { MsgGroupSendMsgAdminPreview } from '../messages/msg-group-send-msg-admin';
import { MsgGroupSendMsgMemberPreview } from '../messages/msg-group-send-msg-member';
import { MsgGroupRestrictedActionsPreview } from '../messages/msg-group-restricted-actions';
import { MsgGroupRestrictedActionsOffPreview } from '../messages/msg-group-restricted-actions-off';
import { MsgGroupRestrictedActionsOffAllPreview } from '../messages/msg-group-restricted-actions-off-all';
import { MsgGroupScreenshotWarningPreview } from '../messages/msg-group-screenshot-warning';
import { MsgGroupScreenRecordingWarningPreview } from '../messages/msg-group-screen-recording-warning';

export interface MsgsPreviewItemProps {
  message: Msg;
  session: Session;
}

export const MsgsPreviewItem = memo(function MsgsPreviewItem({
  message,
  session,
}: MsgsPreviewItemProps) {
  const children = useMemo(() => {
    switch (message.status) {
      case MessageStatus.FailedEncrypt:
      case MessageStatus.FailedDecrypt:
        return <MsgEncryptFailedPreview message={message} />;
    }

    switch (message.payload_type) {
      case MsgPayloadType.TEXT:
      case MsgPayloadType.CARD:
        return <MsgTextPreview message={message} />;
      case MsgPayloadType.IMAGE:
        return <MsgImagePreview message={message} />;
      case MsgPayloadType.FILE:
        return <MsgFilePreview message={message} />;
      case MsgPayloadType.STICKER:
        return <MsgStickerPreview message={message} />;
      case MsgPayloadType.MEDIA_AUDIO_CALL:
        return <MsgMediaCallPreview message={message} />;
      case MsgPayloadType.CALL_HANG_UP:
        return <MsgMediaHangUpPreview message={message} />;
      case MsgPayloadType.CALL_TIMEOUT:
        return <MsgMediaCallTimeoutPreview message={message} />;
      case MsgPayloadType.CALL_CANCELLED:
        return <MsgMediaCallCancelledPreview message={message} />;
      case MsgPayloadType.CALL_REJECTED:
        return <MsgMediaCallRejectedPreview message={message} />;
      case MsgPayloadType.CALL_NETWORK_ERROR:
        return <MsgMediaCallNetworkErrorPreview message={message} />;
      case MsgPayloadType.GROUP_CREATE:
        return <MsgGroupCreatePreview message={message} />;
      case MsgPayloadType.GROUP_UPDATE_NAME:
        return <MsgGroupUpdatePreview message={message} />;
      case MsgPayloadType.GROUP_UPDATE_DESCRIPTION:
        return <MsgGroupUpdateDescriptionPreview message={message} />;
      case MsgPayloadType.GROUP_RESTRICTED:
        return <MsgGroupRestrictedPreview message={message} />;
      case MsgPayloadType.GROUP_UNRESTRICTED:
        return <MsgGroupUnrestrictedPreview message={message} />;
      case MsgPayloadType.GROUP_ADMIN_ONLY_SEND:
        return <MsgGroupSendMsgAdminPreview message={message} />;
      case MsgPayloadType.GROUP_EXCEPTION_SEND:
        return <MsgGroupSendMsgMemberPreview message={message} />;
      case MsgPayloadType.GROUP_RESTRICTED_MODE_ON:
        return <MsgGroupRestrictedActionsPreview message={message} />;
      case MsgPayloadType.GROUP_RESTRICTED_MODE_OFF:
        return <MsgGroupRestrictedActionsOffPreview message={message} />;
      case MsgPayloadType.GROUP_RESTRICTED_MODE_OFF_All:
        return <MsgGroupRestrictedActionsOffAllPreview message={message} />;
      case MsgPayloadType.GROUP_SCREENSHOT_WARNING:
        return <MsgGroupScreenshotWarningPreview message={message} />;
      case MsgPayloadType.GROUP_SCREEN_RECORDING_WARNING:
        return <MsgGroupScreenRecordingWarningPreview message={message} />;
      case MsgPayloadType.GROUP_DISBAND:
        return <MsgGroupDisbandPreview message={message} />;
      case MsgPayloadType.GROUP_INVITE:
        return <MsgGroupInvitePreview message={message} />;
      case MsgPayloadType.GROUP_REMOVE_MEMBER:
        return <MsgGroupRemoveMemberPreview message={message} />;
      case MsgPayloadType.GROUP_MEMBER_QUIT:
        return <MsgGroupMemberQuitPreview message={message} />;
      case MsgPayloadType.GROUP_MEDIA_CLOSE:
        return <MsgGroupMediaClosePreview message={message} />;
      case MsgPayloadType.GROUP_UPDATE_JOIN:
      case MsgPayloadType.GROUP_JOIN_SUCCESS:
        return <MsgGroupUpdateJoinPreview message={message} />;
      case MsgPayloadType.GROUP_TRANSFER_OWNER:
        return <MsgGroupUpdateInfoPreview message={message} />;
      case MsgPayloadType.GROUP_UPDATE_ADMINS:
        return <MsgGroupUpdateAdminsPreview message={message} />;
      case MsgPayloadType.EXPIRE_DURATION:
        return <MsgExpireDurationPreview message={message} />;
      case MsgPayloadType.LUCKY_BOX_CLAIMED:
        return <MsgLuckyBoxClaimedPreview message={message} />;
      case MsgPayloadType.LUCKY_BOX:
        return <MsgLuckyBoxPreview message={message} />;
      case MsgPayloadType.TRANSFER:
        return <MsgTransferPreview message={message} session={session} />;
    }

    return <MsgUnknownPreview message={message} />;
  }, [session, message]);

  return children;
});
